/**
 * Slippy-map tile arithmetic for a small printable map.
 *
 * The report cannot use Leaflet. It is web-only, lazily required, and loads
 * tiles asynchronously, so the browser's Print dialog regularly fires before
 * the map has drawn. A fixed grid of plain <img> tags has none of that
 * problem: the browser treats them like any other image and waits for them.
 *
 * `Structure.geometry` is never populated by any write path, so there is no
 * parcel outline to draw. The centroid is all there is, and a marker over the
 * centre tile is the honest rendering of it.
 */
export const TILE = 256;
export const OSM_ATTRIBUTION = '© OpenStreetMap contributors';

export interface StaticMapTile {
  url: string;
  /** Offset within the composite, in pixels from its top-left. */
  left: number;
  top: number;
}

export interface StaticMapSpec {
  tiles: StaticMapTile[];
  width: number;
  height: number;
  /** Where the centroid falls inside the composite. */
  markerLeft: number;
  markerTop: number;
  attribution: string;
}

export function lonToTileX(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * 2 ** zoom;
}

export function latToTileY(lat: number, zoom: number): number {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom;
}

/**
 * The subdomain is REQUIRED, not decorative: the CSP in firebase.json and
 * app/+html.tsx allows `https://*.tile.openstreetmap.org`, and a wildcard
 * does not match the bare host. A bare tile.openstreetmap.org URL is blocked
 * and the map renders blank with no other symptom. `x` must already be wrapped.
 */
export function tileUrl(zoom: number, x: number, y: number): string {
  const subdomain = 'abc'[Math.abs(x + y) % 3];
  return `https://${subdomain}.tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
}

/**
 * A `grid` x `grid` composite centred on the point. Odd sizes only, so there
 * is a middle tile for the marker to sit on.
 */
export function buildStaticMap(
  latitude: number,
  longitude: number,
  { zoom = 17, grid = 3 }: { zoom?: number; grid?: number } = {}
): StaticMapSpec {
  const span = grid % 2 === 1 ? grid : grid + 1;
  const half = Math.floor(span / 2);

  const exactX = lonToTileX(longitude, zoom);
  const exactY = latToTileY(latitude, zoom);
  const centerX = Math.floor(exactX);
  const centerY = Math.floor(exactY);

  const tiles: StaticMapTile[] = [];
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) {
      const x = centerX + dx;
      const y = centerY + dy;
      // Wrap in x, clamp in y: the poles have no tiles to wrap to.
      const max = 2 ** zoom;
      if (y < 0 || y >= max) continue;
      const wrappedX = ((x % max) + max) % max;
      tiles.push({
        url: tileUrl(zoom, wrappedX, y),
        left: (dx + half) * TILE,
        top: (dy + half) * TILE,
      });
    }
  }

  return {
    tiles,
    width: span * TILE,
    height: span * TILE,
    markerLeft: (half + (exactX - centerX)) * TILE,
    markerTop: (half + (exactY - centerY)) * TILE,
    attribution: OSM_ATTRIBUTION,
  };
}
